<template>
  <DashboardLayout pageTitle="Patient Analytics">
    <div class="max-w-[1200px] mx-auto w-full">
      
      <div class="flex items-center gap-2 mb-2">
        <router-link to="/dashboard" class="text-pharma-text-muted text-sm font-semibold hover:text-primary transition-colors">Dashboard</router-link>
        <span class="material-symbols-outlined text-pharma-text-muted text-base">chevron_right</span>
        <span class="text-primary text-sm font-bold">Analytics</span>
      </div>

      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 class="text-pharma-text-main text-[14px] font-black tracking-tight leading-tight">Patient Analytics</h2>
          <p class="text-pharma-text-muted text-sm mt-1 font-medium">Monitor real-time patient status and medication efficacy across departments.</p>
        </div>
        <button 
          @click="generateReport"
          :disabled="exporting || loading"
          class="flex items-center justify-center rounded-lg h-10 px-5 bg-primary text-white text-sm font-bold hover:bg-primary/90 shadow-sm transition-all disabled:opacity-70"
        >
          <span v-if="exporting" class="material-symbols-outlined mr-2 text-[20px] animate-spin">progress_activity</span>
          <span v-else class="material-symbols-outlined mr-2 text-[20px]">analytics</span>
          {{ exporting ? 'Exporting...' : 'Generate Report' }}
        </button>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
        <div class="flex flex-col lg:flex-row justify-between items-center gap-4">
          <div class="flex w-full lg:w-auto">
            <h3 class="text-sm font-bold text-pharma-text-main">All Patients Overview</h3>
          </div>

          <div class="flex flex-wrap gap-2 w-full lg:w-auto justify-end relative">
            <button 
              @click="toggleDateDropdown"
              class="flex h-10 items-center gap-2 bg-gray-100 border border-transparent px-4 rounded-lg text-sm font-bold text-pharma-text-main hover:bg-gray-200 transition-all"
            >
              <span class="material-symbols-outlined text-base">calendar_today</span>
              {{ selectedRange }}
              <span class="material-symbols-outlined text-base transition-transform" :class="{ 'rotate-180': showDateDropdown }">
                keyboard_arrow_down
              </span>
            </button>

            <div v-if="showDateDropdown" class="absolute top-full right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
              <div class="p-2 space-y-1">
                <button 
                  v-for="option in ['Date Today', 'Last 30 Days']" 
                  :key="option"
                  @click="selectDateRange(option)"
                  class="w-full text-left px-4 py-2.5 text-sm font-bold rounded-lg transition-colors"
                  :class="selectedRange === option && !isCustom ? 'bg-primary text-white' : 'text-pharma-text-main hover:bg-gray-50'"
                >
                  {{ option }}
                </button>

                <button @click="isCustom = !isCustom" class="w-full text-left px-4 py-2.5 text-sm font-bold rounded-lg transition-colors" :class="isCustom ? 'text-primary bg-primary/5' : 'text-pharma-text-main hover:bg-gray-50'">
                  Custom Date
                </button>

                <div v-if="isCustom" class="p-4 space-y-4 bg-gray-50 rounded-lg mt-2 border border-gray-100">
                  <div class="space-y-1">
                    <label class="text-xs font-bold text-pharma-text-main">Start Date</label>
                    <input v-model="customDates.start" type="date" class="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:border-primary outline-none" />
                  </div>
                  <div class="space-y-1">
                    <label class="text-xs font-bold text-pharma-text-main">End Date</label>
                    <input v-model="customDates.end" type="date" class="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:border-primary outline-none" />
                  </div>
                  <button @click="applyCustomDate" class="w-full py-2 bg-primary text-white text-xs font-bold rounded-lg shadow-md">
                    Apply Range
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
        <table class="w-full text-left border-collapse table-fixed">
          <thead>
            <tr class="bg-gray-50/50 border-b border-gray-200">
              <th @click="sortBy('painScale')" class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[180px] cursor-pointer hover:text-primary transition-colors select-none">
                <div class="flex items-center gap-1">Pain Scale <span class="material-symbols-outlined text-xs">{{ getSortIcon('painScale') }}</span></div>
              </th>
              <th @click="sortBy('name')" class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[240px] cursor-pointer hover:text-primary transition-colors select-none">
                <div class="flex items-center gap-1">Patient Name <span class="material-symbols-outlined text-xs">{{ getSortIcon('name') }}</span></div>
              </th>
              <th @click="sortBy('medication')" class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[240px] cursor-pointer hover:text-primary transition-colors select-none">
                <div class="flex items-center gap-1">Medication <span class="material-symbols-outlined text-xs">{{ getSortIcon('medication') }}</span></div>
              </th>
              <th @click="sortBy('doctor')" class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[200px] cursor-pointer hover:text-primary transition-colors select-none">
                <div class="flex items-center gap-1">Assigned Doctor <span class="material-symbols-outlined text-xs">{{ getSortIcon('doctor') }}</span></div>
              </th>
              <th @click="sortBy('dateCreated')" class="px-6 py-4 text-sm font-bold text-pharma-text-muted text-right w-[160px] cursor-pointer hover:text-primary transition-colors select-none">
                <div class="flex items-center justify-end gap-1">Date Created <span class="material-symbols-outlined text-xs">{{ getSortIcon('dateCreated') }}</span></div>
              </th>
            </tr>
          </thead>
          
          <tbody class="divide-y divide-gray-100">
            <tr v-if="loading">
              <td colspan="5" class="px-6 py-20 text-center">
                <span class="material-symbols-outlined animate-spin text-primary text-4xl">progress_activity</span>
                <p class="text-sm text-pharma-text-muted mt-2 font-bold tracking-tight">Aggregating records...</p>
              </td>
            </tr>

            <tr v-else v-for="patient in sortedData" :key="patient.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4">
                <div class="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1 w-fit border border-gray-100">
                  <span :class="`w-2 h-2 rounded-full ${patient.painColor}`"></span>
                  <span class="text-sm font-bold text-pharma-text-main">{{ patient.painScale }}</span>
                </div>
              </td>
              <td class="px-6 py-4">
                <p class="text-sm font-bold text-pharma-text-main tracking-tight">{{ patient.name }}</p>
              </td>
              <td class="px-6 py-4">
                <span class="bg-gray-100 text-pharma-text-muted px-2.5 py-1 rounded text-xs font-bold">
                  {{ patient.medication }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-pharma-text-muted font-medium">Dr. {{ patient.doctor }}</td>
              <td class="px-6 py-4 text-right">
                <span class="text-sm text-pharma-text-muted font-medium">{{ patient.dateCreated }}</span>
              </td>
            </tr>

            <tr v-if="!loading && sortedData.length === 0">
              <td colspan="5" class="px-6 py-20 text-center text-pharma-text-muted italic font-medium">No records found for this range.</td>
            </tr>
          </tbody>
        </table>

        <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-white">
          <p class="text-sm text-pharma-text-muted font-medium">Showing {{ sortedData.length }} results</p>
          <div class="flex gap-2">
            <button class="px-4 py-1.5 border border-gray-200 rounded-lg text-sm font-bold text-pharma-text-main hover:bg-gray-50">Previous</button>
            <button class="px-4 py-1.5 border border-gray-200 rounded-lg text-sm font-bold text-pharma-text-main hover:bg-gray-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './layouts/DashboardLayout.vue'
import api from '@/services/api'

export default {
  name: 'PatientAnalytics',
  components: { DashboardLayout },
  data() {
    return {
      loading: true,
      exporting: false,
      showDateDropdown: false,
      selectedRange: 'Last 30 Days',
      isCustom: false,
      customDates: { start: '', end: '' },
      currentSort: 'dateCreated',
      currentSortDir: 'desc',
      analyticsData: []
    }
  },
  computed: {
    sortedData() {
      if (!this.analyticsData) return [];
      return [...this.analyticsData].sort((a, b) => {
        let modifier = this.currentSortDir === 'desc' ? -1 : 1;
        let valA, valB;

        if (this.currentSort === 'dateCreated') {
          valA = new Date(a.dateCreated);
          valB = new Date(b.dateCreated);
        } else {
          valA = (a[this.currentSort] || '').toString().toLowerCase();
          valB = (b[this.currentSort] || '').toString().toLowerCase();
        }

        if (valA < valB) return -1 * modifier;
        if (valA > valB) return 1 * modifier;
        return 0;
      });
    }
  },
  watch: {
    selectedRange() {
      this.fetchAnalytics();
    }
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      try {
        const response = await api.get('/analytics/patient-overview', {
          params: {
            range: this.selectedRange,
            start: this.customDates.start,
            end: this.customDates.end
          }
        });
        this.analyticsData = response.data.records || response.data;
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        this.loading = false;
      }
    },
    async generateReport() {
      this.exporting = true;
      try {
        const response = await api.get('/analytics/export-csv', {
          params: {
            range: this.selectedRange,
            start: this.customDates.start,
            end: this.customDates.end
          },
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Patient_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        alert("Report generation failed.");
      } finally {
        this.exporting = false;
      }
    },
    sortBy(s) {
      if (s === this.currentSort) {
        this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSort = s;
        this.currentSortDir = 'asc';
      }
    },
    getSortIcon(s) {
      if (this.currentSort !== s) return 'unfold_more';
      return this.currentSortDir === 'asc' ? 'expand_less' : 'expand_more';
    },
    toggleDateDropdown() {
      this.showDateDropdown = !this.showDateDropdown;
      if (!this.showDateDropdown) this.isCustom = false;
    },
    selectDateRange(option) {
      this.selectedRange = option;
      this.isCustom = false;
      this.showDateDropdown = false;
    },
    applyCustomDate() {
      if (this.customDates.start && this.customDates.end) {
        this.selectedRange = 'Custom Range';
        this.showDateDropdown = false;
        this.fetchAnalytics();
      }
    }
  },
  mounted() {
    this.fetchAnalytics();
  }
}
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>